import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { DatasetState } from "../../../types/types";

// =====================
// CONSTANTS
// =====================
export const FILE_ACCEPT = ".csv,.xls,.xlsx";
export const MAX_MB = 25;

// =====================
// URL VALIDATION
// =====================
export const isValidUrl = (value: string) => {
  try {
    const url = new URL(value.trim());
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

export const isExcelOrCsvUrl = (value: string) => {
  const url = value.trim().toLowerCase();

  return [".csv", ".xls", ".xlsx"].some((ext) => url.includes(ext));
};

export const isLikelyApiUrl = (value: string) => {
  const url = value.trim().toLowerCase();

  return (
    url.includes("/api/") ||
    url.includes("json") ||
    url.includes("format=json") ||
    url.includes("restcountries.com") ||
    url.includes("kobo") ||
    url.includes("kapsurvey")
  );
};

export const isSupportedExternalSource = (value: string) => {
  if (!isValidUrl(value)) return false;

  return isExcelOrCsvUrl(value) || isLikelyApiUrl(value);
};

// =====================
// DATA INFERENCE
// =====================
export function inferType(v: any): "number" | "string" | "date" {
  if (v == null || v === "") return "string";
  if (typeof v === "number") return "number";

  const asNum = Number(v);
  if (!Number.isNaN(asNum) && v !== "") return "number";

  const maybe = dayjs(v);
  if (maybe.isValid() && (String(v).includes("-") || String(v).includes("/"))) {
    return "date";
  }

  return "string";
}

export function inferSchema(rows: any[]): DatasetState["schema"] {
  const first = rows[0] || {};

  return Object.keys(first).map((k) => ({
    field: k,
    type: inferType(first[k]),
  }));
}

// =====================
// EXCEL / CSV PARSER
// =====================
export async function parseExcelFile(file: File): Promise<any[]> {
  const data = await file.arrayBuffer();
  const wb = XLSX.read(data);
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });

  return rows as any[];
}
